import { Button, Col, Form, Input, Row, Select, Steps } from 'antd'
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import regex from '../../../../constants/system.keys'
import * as SERVICE from '../../../../constants/service.types'

import './index.scss'
import { useDispatch, useSelector } from 'react-redux'
import {
  addExternalDeviceReturn,
  checkExternalDeviceReturn,
  socketEmitEvent,
  updateDeviceReturn,
} from '../../../../redux/actions'
import { CAMERA_TYPE, EDEVICETYPE } from '../../../../constants'
import DownloadConfigComponent from './DownloadConfig'
import InputTag from '../../../../components/InputTag'
import LocationSearchLocationIQ from '../../../../components/LocationSearchLocationIQ'

const { Step } = Steps
const { Option } = Select

const AddBodyCameraComponent = ({
  onDone,
  gatewaySerial,
  gatewayLocalAddress,
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useDispatch()

  const {
    namespace,
    checkExternalDevice,
    userId,
    addExternalDevice,
    listPlace,
    updateDevice,
  } = useSelector((state) => state)

  const [requestId, setRequestId] = useState()
  const [formData, setFormData] = useState({})
  const [cameraConfig, setCameraConfig] = useState('')

  const onCreate = useCallback(
    (values, requestId) => {
      if (!namespace || !userId) return
      const data = {
        serial: values.serial,
        namespace,
        extraInfo: {
          typeCamera: CAMERA_TYPE.BODY_WARM_AXIS,
          name: values.name,
          accountId: userId,
          gatewaySerial,
          factorySerial: values.cameraId,
          controllerId: values.controllerId,
          localAddress: gatewayLocalAddress,
        },
      }

      const dataObj = {
        appServiceType: SERVICE.REQUEST_ADD_EXTERNAL_DEVICE,
        requestId,
        body: JSON.stringify(data),
      }
      setIsLoading(true)
      return dispatch(socketEmitEvent(dataObj))
    },
    [namespace, userId, gatewayLocalAddress, dispatch, gatewaySerial]
  )

  const onUpdate = useCallback(
    (values, requestId) => {
      const { tags, placeId } = values
      if (formData.serial) {
        const data = {
          note: formData.location?.note ?? '',
          lat: formData.location?.lat ?? 0,
          lng: formData.location?.lng ?? 0,
          serial: formData.serial,
          namespace,
          tags: tags?.join(';')?.toLowerCase() || ';',
          placeId,
        }

        const dataObj = {
          appServiceType: SERVICE.UPDATE_DEVICE,
          requestId,
          body: JSON.stringify(data),
        }

        return dispatch(socketEmitEvent(dataObj))
      }
    },
    [formData, namespace, dispatch]
  )

  const onCheck = useCallback(
    (serial, requestId) => {
      if (serial && serial.length > 0) {
        let data = {
          serial: serial.trim(),
          namespace,
          deviceType: EDEVICETYPE.CAMERA,
          extraInfo: {
            factorySerial: serial.trim(),
          },
        }

        let dataObj = {
          appServiceType: SERVICE.CHECK_EXTERNAL_DEVICE,
          requestId: requestId || new Date().getTime(),
          body: JSON.stringify(data),
        }
        console.log('🚀 ~ ExpandData ~ dataObj:', dataObj)
        return dispatch(socketEmitEvent(dataObj))
      }
    },
    [dispatch, namespace]

  );

  const onFinish = (values) => {
    setIsLoading(true)
    const timeRequest = new Date().getTime()
    setRequestId(timeRequest)

    if (currentStep === 0) {
      setFormData((prev) => ({
        ...prev,
        ...values,
        controllerId: values.controllerId,
      }))
      onCheck && onCheck(values.cameraId || '', timeRequest)
      return
    }

    if (currentStep === 1) {
      const newFormData = {
        ...formData,
        ...values,
      }
      setFormData(newFormData)
      onCreate(newFormData, timeRequest)
      return
    }

    if (currentStep === 2) {
      setFormData((prev) => ({
        ...prev,
        tags: values.tags,
        placeId: values.placeId,
      }))
      onUpdate(values, timeRequest)
    }
  }

  const handleBack = () => {
    form.resetFields()
    if (currentStep === 1) {
      setFormData({})
      form.setFieldsValue({})
    }
    setCurrentStep((prev) => (prev - 1 >= 0 ? prev - 1 : 0))
    setIsLoading(false)
  }

  const handleAddressChange = (address) => {
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        note: address,
      },
    }))
  }

  const handleSelectAddress = (address) => {
    setFormData((prev) => ({
      ...prev,
      location: address,
    }))
  }

  useEffect(() => {
    if (formData) {
      form.setFieldsValue({
        ...form.getFieldsValue(),
        ...formData,
      })
    }
  }, [form, formData])

  useEffect(() => {
    setIsLoading(false)
    if (checkExternalDevice && checkExternalDevice.requestId === requestId) {
      console.log('🚀 ~ AddBodyCameraComponent ~ checkExternalDevice:', checkExternalDevice)
      if (checkExternalDevice.result === 1) {
        const data = JSON.parse(checkExternalDevice.data || '{}')
        setFormData((prev) => ({
          ...prev,
          serial: data.serial,
        }))
        setCurrentStep(1)
        dispatch(checkExternalDeviceReturn(null))
      } else {
        form.setFields([
          {
            name: 'controllerId',
            errors: ['Some thing went wrong'],
          },
        ])
      }
    }
  }, [checkExternalDevice, dispatch, form, requestId])

  useEffect(() => {
    if (addExternalDevice) {
      if (addExternalDevice?.serial === formData.serial) {
        setIsLoading(false)
        dispatch(addExternalDeviceReturn(null))
        setCameraConfig(JSON.stringify(addExternalDevice?.cameraConfig, null, 2))
        setCurrentStep(2)
      }
    }
  }, [addExternalDevice, dispatch, formData])

  useEffect(() => {
    if (
      updateDevice &&
      updateDevice?.requestId === requestId &&
      updateDevice?.isSuccess &&
      updateDevice?.serial === formData.serial
    ) {
      setCurrentStep(3)
      dispatch(updateDeviceReturn(null))
    }
  }, [updateDevice, requestId, formData, dispatch])

  return (
    <div className="add-body-camera-component">
      <div className="steps-add-camera">
        <Steps size="small" current={currentStep}>
          <Step title={t('Scan')} />
          <Step title={t('Create')} />
          <Step title={t('Update')} />
          <Step title={t('Configuration')} />
        </Steps>

        <div className="form-wrapper">
          <Form
            form={form}
            name="form-validate"
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            onFinish={onFinish}
          >
            <Row gutter={16}>
              {(currentStep === 0 || currentStep === 1) && (
                <Col xs={24}>
                  <Form.Item
                    name="controllerId"
                    label={t('Controller ID')}
                    initialValue={currentStep === 0 ? '' : formData.controllerId || ''}
                    rules={[
                      { required: true, message: t('Please enter controller id!') },
                      { pattern: regex.isTextOrNumberNoLimit, message: t('Please enter only letters and numbers!') },
                      { max: 80, message: t('Please enter up to 80 characters!') },
                    ]}
                  >
                    <Input placeholder={t('Enter controller id')} disabled={currentStep !== 0} />
                  </Form.Item>

                  <Form.Item
                    name="cameraId"
                    label={t('Camera ID')}
                    initialValue={currentStep === 0 ? '' : formData.controllerId || ''}
                    rules={[
                      { required: true, message: t('Please enter camera id!') },
                      { pattern: regex.isTextOrNumberNoLimit, message: t('Please enter only letters and numbers!') },
                      { max: 80, message: t('Please enter up to 80 characters!') },
                    ]}
                  >
                    <Input placeholder={t('Enter camera id')} disabled={currentStep !== 0} />
                  </Form.Item>
                </Col>
              )}

              {currentStep === 1 && (
                <Fragment>
                  <Col xs={24}>
                    <Form.Item name="serial" label={t('Serial')} initialValue={formData.serial || ''}>
                      <Input placeholder={t('Enter device serial')} disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name="name"
                      label={t('Name')}
                      rules={[
                        { required: true, message: t('Please enter camera name!') },
                        { max: 80, message: t('Please enter up to 80 characters!') },
                      ]}
                    >
                      <Input placeholder={t('Enter camera name')} disabled={!!cameraConfig} />
                    </Form.Item>
                  </Col>
                </Fragment>
              )}

              {currentStep === 2 && (
                <Fragment>
                  <Col xs={24}>
                    <Form.Item name="location" label={t('Location')}>
                      <LocationSearchLocationIQ
                        address={formData.location?.note}
                        clearAddress={() => setFormData((prev) => ({ ...prev, location: {} }))}
                        onChange={handleAddressChange}
                        onAddressSelect={handleSelectAddress}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name="placeId" label="Place">
                      <Select placeholder="Select Place" getPopupContainer={(trigger) => trigger.parentElement}>
                        {listPlace?.places?.map((place) => (
                          <Option key={place?.placeId} value={place?.placeId}>
                            {place?.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name="tags" label={t('Tag')}>
                      <InputTag numberTag={3} nameAdd="Add tag" />
                    </Form.Item>
                  </Col>
                </Fragment>
              )}
            </Row>

            {cameraConfig && currentStep === 3 && <DownloadConfigComponent config={cameraConfig} />}

            <div className="button-groups">
              {currentStep === 0 && (
                <Form.Item shouldUpdate>
                  <Button htmlType="submit" type="primary" loading={isLoading}>
                    {t('Scan')}
                  </Button>
                </Form.Item>
              )}

              {currentStep === 1 && (
                <>
                  <Button type="default" onClick={handleBack}>
                    {t('Back')}
                  </Button>
                  <Form.Item shouldUpdate>
                    <Button htmlType="submit" type="primary" loading={isLoading}>
                      {t('Create')}
                    </Button>
                  </Form.Item>
                </>
              )}

              {currentStep === 2 && (
                <Fragment>
                  <Button type="default" onClick={() => setCurrentStep(3)}>
                    {t('Skip')}
                  </Button>
                  <Form.Item shouldUpdate>
                    <Button htmlType="submit" type="primary" loading={isLoading}>
                      {t('Update')}
                    </Button>
                  </Form.Item>
                </Fragment>
              )}

              {currentStep === 3 && (
                <Form.Item shouldUpdate>
                  <Button type="primary" onClick={() => onDone()}>
                    {t('Done')}
                  </Button>
                </Form.Item>
              )}
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default AddBodyCameraComponent
